import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, CheckCircle2, X } from "lucide-react";

function ymd(d) { return d.toISOString().slice(0, 10); }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addDays(d, n) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }

function buildMonthGrid(anchor) {
    const first = startOfMonth(anchor);
    const last = endOfMonth(anchor);
    const startWeekday = (first.getDay() + 6) % 7;
    const start = addDays(first, -startWeekday);
    const days = [];
    let cur = start;
    while (cur <= last || days.length % 7 !== 0) {
        days.push(cur);
        cur = addDays(cur, 1);
    }
    return days;
}

export default function ClientCalendarPage() {
    const [anchor, setAnchor] = useState(new Date());
    const [workouts, setWorkouts] = useState([]);
    const [selected, setSelected] = useState(null);
    const days = useMemo(() => buildMonthGrid(anchor), [anchor]);

    const load = async () => {
        const start = ymd(days[0]);
        const end = ymd(days[days.length - 1]);
        const { data } = await api.get("/me/workouts", { params: { start, end } });
        setWorkouts(data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor]);

    const byDay = useMemo(() => {
        const m = {};
        for (const w of workouts) (m[w.date] ||= []).push(w);
        return m;
    }, [workouts]);

    const complete = async (wid) => {
        await api.post(`/me/workouts/${wid}/complete`);
        load();
        setSelected((s) => s ? { ...s, completed: true } : s);
    };

    const monthLabel = anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

    return (
        <div className="p-8 space-y-6" data-testid="client-calendar-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny mb-2">/ What's coming</p>
                    <h1 className="font-serif text-6xl">Schedule</h1>
                </div>
            </div>

            <div className="border border-[var(--border)] bg-[var(--surface)]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
                    <h2 className="font-serif text-3xl" data-testid="cl-cal-month-label">{monthLabel}</h2>
                    <div className="flex items-center gap-2">
                        <button className="btn-ghost p-2" onClick={() => setAnchor(addDays(startOfMonth(anchor), -1))} data-testid="cl-cal-prev"><ChevronLeft size={16} /></button>
                        <button className="btn-ghost px-3" onClick={() => setAnchor(new Date())} data-testid="cl-cal-today">TODAY</button>
                        <button className="btn-ghost p-2" onClick={() => setAnchor(addDays(endOfMonth(anchor), 1))} data-testid="cl-cal-next"><ChevronRight size={16} /></button>
                    </div>
                </div>

                <div className="grid grid-cols-7 border-b border-[var(--border)]">
                    {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
                        <div key={d} className="px-3 py-2 label-tiny border-r border-[var(--border)] last:border-r-0">{d}</div>
                    ))}
                </div>

                <div className="grid grid-cols-7">
                    {days.map((d, i) => {
                        const inMonth = d.getMonth() === anchor.getMonth();
                        const isToday = ymd(d) === ymd(new Date());
                        const items = byDay[ymd(d)] || [];
                        return (
                            <button key={i} onClick={() => items[0] && setSelected(items[0])} data-testid={`cl-cal-day-${ymd(d)}`}
                                className={`text-left min-h-[110px] p-2 border-r border-b border-[var(--border)] hover:bg-[var(--surface-hover)] ${
                                    inMonth ? "" : "opacity-40"
                                } ${isToday ? "bg-stripes" : ""}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`font-display text-2xl ${isToday ? "text-[var(--gold)]" : ""}`}>{d.getDate()}</span>
                                    {items.length > 0 && <span className="font-mono text-[10px] text-[var(--text-muted)]">{items.length}</span>}
                                </div>
                                <div className="space-y-1">
                                    {items.slice(0, 2).map((w) => (
                                        <div key={w.id} className={`text-[11px] truncate border-l-2 pl-2 ${w.completed ? "border-[var(--gold-bright)] line-through text-[var(--text-muted)]" : "border-[var(--gold)]"}`}>
                                            {w.title}
                                        </div>
                                    ))}
                                    {items.length > 2 && <div className="text-[10px] text-[var(--text-muted)] font-mono">+{items.length - 2}</div>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <p className="text-xs text-[var(--text-muted)] font-mono">
                Tap any day to see details and mark complete. Future sessions populate as your coach programs them.
            </p>

            {/* Detail drawer */}
            {selected && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="cl-workout-detail">
                    <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                            <div>
                                <p className="label-tiny mb-1">/ {new Date(selected.date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</p>
                                <h3 className="font-serif text-2xl">{selected.title}</h3>
                            </div>
                            <button onClick={() => setSelected(null)}><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="font-mono text-xs text-[var(--text-muted)]">{selected.time || "—"} · {selected.duration_min}m · {selected.type}</p>
                            {selected.description && <pre className="whitespace-pre-wrap text-sm text-[var(--text-muted)] font-mono bg-[var(--bg-deep)] p-3">{selected.description}</pre>}
                            {selected.completed ? (
                                <span className="tag-pill border-[var(--gold-bright)] text-[var(--gold-bright)]">DONE</span>
                            ) : (
                                <button onClick={() => complete(selected.id)} className="btn-primary inline-flex items-center gap-2 w-full justify-center">
                                    <CheckCircle2 size={14} /> Mark complete
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
